/**
 * Menu items and small visuals shared by both saved-view presentations
 * (desktop strip pill menus and the mobile dropdown), so the two never drift.
 */

import {
  CheckCircle,
  Edit01,
  FlipBackward,
  Globe01,
  Lock01,
  Save01,
  Trash01,
} from '@untitledui/icons';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import type { SavedFilterView } from '@/types/savedFilterView';

import type { DirtyViewActions } from './savedViewTypes';

// ---------------------------------------------------------------------------
// Shared pill styling (strip pills and the mobile trigger look identical)
// ---------------------------------------------------------------------------

export const PILL_BASE =
  'group/pill inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium transition-colors';

// Active = a crisp white chip: a delicate hairline border (gray-100) + a
// whisper shadow (5% opacity) lift it off the header so the selection reads as
// "designed" rather than a flat grey blob, while staying obvious. Inactive =
// muted text that fills with a faint gray on hover.
export const PILL_ACTIVE =
  'bg-[var(--color-bg-primary)] text-fg-primary shadow-xs ring-1 ring-inset ring-[var(--color-border-tertiary)]';

export const PILL_INACTIVE =
  'text-fg-tertiary hover:bg-[var(--color-bg-secondary)] hover:text-fg-secondary';

// 20% smaller than the previous size-4 (1rem) → 0.8rem.
export const VIEW_ICON_CLASS = 'size-[0.8rem] shrink-0';

export const CARET_ICON_CLASS = 'size-[0.7rem]';

const DEFAULT_VIEW_ICON_CLASS = 'size-[0.7rem] shrink-0 text-fg-brand-primary';

// "Edited" dot on the active saved-view pill while it has unsaved changes.
const DIRTY_DOT_CLASS =
  'size-1.5 shrink-0 rounded-full bg-[var(--color-brand-600)]';

// ---------------------------------------------------------------------------
// DefaultViewMarker — accessible indicator shown on the pill matching the
// user's default view. Follows the established icon+Tooltip a11y-label
// pattern (see EnrichIcon.tsx) rather than a bare icon, which would read as
// "favorite" to a screen reader.
// ---------------------------------------------------------------------------

export function DefaultViewMarker() {
  return (
    <Tooltip title='Default view' placement='top'>
      <TooltipTrigger>
        <span
          className='inline-flex shrink-0 items-center justify-center'
          aria-label='Default view'
        >
          <CheckCircle className={DEFAULT_VIEW_ICON_CLASS} />
        </span>
      </TooltipTrigger>
    </Tooltip>
  );
}

export function DirtyDot() {
  return (
    <span
      className={DIRTY_DOT_CLASS}
      title='Unsaved changes'
      aria-label='Unsaved changes'
    />
  );
}

// ---------------------------------------------------------------------------
// DirtyViewMenuItems — "Save changes" / "Discard changes" for the active saved
// view while it diverges from its stored state. Lives in the view's menu
// (not as loose buttons in the header) so it costs no header width; the
// dirty dot on the pill is the persistent signal that they're there.
// ---------------------------------------------------------------------------

interface DirtyViewMenuItemsProps {
  actions: DirtyViewActions;
}

export function DirtyViewMenuItems({ actions }: DirtyViewMenuItemsProps) {
  const { canSave, isSaving, onSave, onSaveAsNew, onDiscard, shortcutHint } =
    actions;
  return (
    <>
      {canSave ? (
        <Dropdown.Item
          id='save-changes'
          icon={Save01}
          label={isSaving ? 'Saving…' : 'Save changes'}
          textValue='Save changes'
          addon={shortcutHint}
          isDisabled={isSaving}
          onAction={onSave}
        />
      ) : (
        <Dropdown.Item
          id='save-as-new'
          icon={Save01}
          label='Save as new view'
          textValue='Save as new view'
          addon={shortcutHint}
          onAction={onSaveAsNew}
        />
      )}
      <Dropdown.Item
        id='discard-changes'
        icon={FlipBackward}
        label='Discard changes'
        textValue='Discard changes'
        isDisabled={isSaving}
        onAction={onDiscard}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// ManageSavedViewMenuItems — rename / share / default / delete for one saved
// view. Rename, share, and delete are gated on ownership; setting a default is
// a personal preference and is offered for team-shared views too.
// ---------------------------------------------------------------------------

interface ManageSavedViewMenuItemsProps {
  view: SavedFilterView;
  isDefault: boolean;
  /** Present only when the parent enabled the default-view feature. */
  showDefaultItem: boolean;
  isMutationPending: boolean;
  onRename: () => void;
  onToggleShare: () => void;
  onToggleDefault: () => void;
  onDelete: () => void;
}

export function ManageSavedViewMenuItems({
  view,
  isDefault,
  showDefaultItem,
  isMutationPending,
  onRename,
  onToggleShare,
  onToggleDefault,
  onDelete,
}: ManageSavedViewMenuItemsProps) {
  const shareLabel = view.is_shared_with_org
    ? 'Make private'
    : 'Share with team';
  const defaultLabel = isDefault ? 'Clear default' : 'Set as default';
  return (
    <>
      {view.is_owner && (
        <Dropdown.Item
          id='rename'
          icon={Edit01}
          label='Rename'
          textValue='Rename'
          onAction={onRename}
        />
      )}
      {view.is_owner && (
        <Dropdown.Item
          id='share'
          icon={view.is_shared_with_org ? Lock01 : Globe01}
          label={shareLabel}
          textValue={shareLabel}
          isDisabled={isMutationPending}
          onAction={onToggleShare}
        />
      )}
      {showDefaultItem && (
        <Dropdown.Item
          id='default'
          icon={CheckCircle}
          label={defaultLabel}
          textValue={defaultLabel}
          onAction={onToggleDefault}
        />
      )}
      {view.is_owner && (
        <Dropdown.Item
          id='delete'
          icon={Trash01}
          label='Delete'
          textValue='Delete'
          onAction={onDelete}
        />
      )}
    </>
  );
}

/** Single "Set as default" / "Clear default" item for the All / built-in views. */
interface DefaultOnlyMenuItemProps {
  isDefault: boolean;
  onToggleDefault: () => void;
}

export function DefaultOnlyMenuItem({
  isDefault,
  onToggleDefault,
}: DefaultOnlyMenuItemProps) {
  const label = isDefault ? 'Clear default' : 'Set as default';
  return (
    <Dropdown.Item
      id='default'
      icon={CheckCircle}
      label={label}
      textValue={label}
      onAction={onToggleDefault}
    />
  );
}
